import type { Row } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import {
  Chart21,
  Edit2,
  More,
  ToggleOffCircle,
  ToggleOnCircle,
  Trash,
} from 'iconsax-react';
import { Button } from '@src/design-system/button';
import { Dialog, DialogContent } from '@src/design-system/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';
import { SingleDeleteAction } from '@src/design-system/molecules/ticlick-table/actions';
import { HasPermission } from '@features/roles/guards/has-permission';
import { entities } from '@shared/constants/entities';
import { useHasPermission } from '@shared/hooks/use-has-permission';
import type { TAnnouncement } from '@src/shared/types/announcement';
import announcementsApi from '../services/announcements-api';
import { CreateOrEditAnnouncementDialog } from '../components/create-or-edit-announcement-dialog';
import { AnnouncementStatisticsDialog } from '../components/announcement-statistics-dialog';
import {
  getStatusForToggleOn,
  isAnnouncementToggleOn,
} from '../utils/announcement-status';

interface AnnouncementsActionsCellProps {
  row: Row<TAnnouncement>;
}

const AnnouncementsActionsCell = ({ row }: AnnouncementsActionsCellProps) => {
  const announcement = row.original;
  const hasPermission = useHasPermission();
  const canChange = hasPermission(entities.announcement, 'change');
  const canDelete = hasPermission(entities.announcement, 'delete');
  const [isToggleOn, setIsToggleOn] = useState(() =>
    isAnnouncementToggleOn(announcement)
  );
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const { mutate: updateAnnouncement, isPending: isUpdating } =
    announcementsApi.useUpdate();
  const { mutateAsync: deleteAnnouncement, isPending: isDeleting } =
    announcementsApi.useDelete();

  useEffect(() => {
    setIsToggleOn(isAnnouncementToggleOn(announcement));
  }, [announcement.status, announcement.is_active]);

  const handleToggle = () => {
    const nextOn = !isToggleOn;
    const previous = isToggleOn;
    setIsToggleOn(nextOn);

    updateAnnouncement(
      {
        id: announcement.id,
        status: nextOn
          ? getStatusForToggleOn(announcement.share_at)
          : 'INACTIVE',
      },
      {
        onError: () => {
          setIsToggleOn(previous);
        },
      }
    );
  };

  const ToggleIcon = isToggleOn ? ToggleOnCircle : ToggleOffCircle;

  return (
    <div className='flex items-center gap-2'>
      <HasPermission
        entity={entities.announcement}
        action='change'
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='iconSm'
              type='button'
              className='text-gray-800'
              disabled={isUpdating || !canChange}
              onClick={handleToggle}
              aria-label={`${isToggleOn ? 'Deactivate' : 'Activate'} ${announcement.name}`}
            >
              <ToggleIcon
                size={20}
                variant='Bulk'
                className={isToggleOn ? 'text-green-500' : 'text-gray-600'}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isToggleOn ? 'Set inactive' : 'Set live'}
          </TooltipContent>
        </Tooltip>
      </HasPermission>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='iconSm'
            type='button'
            className='text-gray-800'
            onClick={() => setIsStatsOpen(true)}
            aria-label={`View statistics for ${announcement.name}`}
          >
            <Chart21
              size={20}
              variant='Bulk'
              className='text-gray-800'
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>View statistics</TooltipContent>
      </Tooltip>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='iconSm'
            type='button'
            className='text-gray-800'
          >
            <More
              size={20}
              variant='Linear'
              className='rotate-90'
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          {canChange && (
            <DropdownMenuItem onSelect={() => setIsEditOpen(true)}>
              <Edit2 size={16} />
              <span className='ml-2'>Edit</span>
            </DropdownMenuItem>
          )}
          {canDelete && (
            <SingleDeleteAction
              id={announcement.id}
              label='announcement'
              isLoading={isDeleting}
              triggerComponent={
                <DropdownMenuItem
                  onSelect={(event) => event.preventDefault()}
                  className='text-red-600 focus:text-red-600'
                >
                  <Trash
                    size={16}
                    variant='TwoTone'
                    className='mr-2'
                  />
                  Delete
                </DropdownMenuItem>
              }
              onConfirm={(closeModal) =>
                deleteAnnouncement(
                  { id: announcement.id },
                  { onSuccess: closeModal }
                )
              }
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      >
        <DialogContent className='max-w-300 w-[95vw] p-0 overflow-hidden'>
          <CreateOrEditAnnouncementDialog announcement={announcement} />
        </DialogContent>
      </Dialog>

      <AnnouncementStatisticsDialog
        announcement={announcement}
        open={isStatsOpen}
        onOpenChange={setIsStatsOpen}
      />
    </div>
  );
};

export default AnnouncementsActionsCell;
